import React, { useState, Fragment } from "react";
import {
  Box,
  TextField,
  MenuItem,
  useTheme,
  Menu,
  Button,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
  SearchRounded,
  SortRounded,
  FilterList,
  CheckRounded,
  FilterAltRounded,
  FilterAltOutlined,
} from "@mui/icons-material";
import { sortOptions } from "./FileExplorer";
import { fileStatus } from "./FilesList/FileStatus";
import { GradientIconLight } from "../../../../components/GradientIcon";
import FileUploadButton from "./FileUploadButton";
import {
  TOGGLE_FILTER_BUTTON_ON_FILE_EXPLORER,
  OPEN_SORT_MENU_ON_FILE_EXPLORER,
} from "../../../../utils/posthogEvents";
import { usePostHog } from "posthog-js/react";
import { useThemeSettings } from "../../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme) => ({
  filtersContainer: {
    paddingTop: 8,
    width: "100%",
    overflow: "auto",
    [theme.breakpoints.up(1050)]: {
      // width: 908,
      margin: "0 auto",
    },
    [theme.breakpoints.up("sm")]: {
      // paddingRight: "32px",
    },
  },
  sortIcon: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  activeViewIcon: {
    background: "#E8EAF6",
  },
  gradientText: {
    background: theme.palette.accentGradient,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  gradientTextLight: {
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  filterButton: {
    background: theme.palette.background.light,
    color: theme.palette.action.active,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
      background: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  selectedFilterButton: {
    background: theme.palette.selection,
    color: theme.palette.text.primary,
  },
}));

function Filters({
  files,
  category,
  status,
  fileType,
  query,
  sortOption,
  view,
  onSearchFiles,
  onSortFiles,
  onViewChange,
  onStatusChange,
  onFileTypeChange,
  onResetFilters,
  onCategoryChange,
}) {
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";
  const { classes } = useStyles();
  const posthog = usePostHog();
  // Only show reset if any filter is active
  const isFilterActive =
    category !== "All" || status !== "all" || fileType !== "all" || query;
  const [showFilters, setShowFilters] = useState(false);

  const handleToggleFilters = () => {
    setShowFilters((v) => !v);
    posthog.capture(TOGGLE_FILTER_BUTTON_ON_FILE_EXPLORER, {
      showFilters: !showFilters,
    });
  };

  return (
    <Box className={classes.filtersContainer}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1.5}>
        <Box width={"100%"} display={"flex"} alignItems={"center"} gap={1.5}>
          <Button
            variant="contained"
            startIcon={
              showFilters ? (
                isDarkMode ? (
                  <FilterAltRounded
                    fontSize="small"
                    sx={{ color: (theme) => theme.palette.text.primary }}
                  />
                ) : (
                  <GradientIconLight
                    Icon={FilterAltRounded}
                    sx={{ fontSize: 20 }}
                  />
                )
              ) : (
                <FilterAltOutlined fontSize="small" />
              )
            }
            onClick={handleToggleFilters}
            className={`${classes.filterButton} ${
              showFilters ? classes.selectedFilterButton : ""
            }`}>
            Filter
          </Button>
          <SortFilter
            sortOption={sortOption}
            onSortFiles={onSortFiles}
            isDarkMode={isDarkMode}
          />
          <SearchFilter query={query} onSearchFiles={onSearchFiles} />
        </Box>
        <FileUploadButton />
      </Box>

      {showFilters && (
        <Box display="flex" alignItems="center" gap={2} mt={3}>
          <CategoryFilter
            files={files}
            category={category}
            onCategoryChange={onCategoryChange}
          />
          <FileTypeFilter
            fileType={fileType}
            onFileTypeChange={onFileTypeChange}
          />
          <StatusFilter status={status} onStatusChange={onStatusChange} />
          {isFilterActive && (
            <Button variant="text" onClick={onResetFilters}>
              <span className={classes.gradientText}>Clear filters</span>
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

function CategoryFilter({ files, category, onCategoryChange }) {
  const fileCategories = [
    "All",
    ...new Set(
      files
        .map((file) => (file.category ? file.category : null))
        .filter((tag) => tag && tag.trim() !== "")
    ),
  ];
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="File Category"
      size="small"
      value={category}
      onChange={(e) => onCategoryChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {fileCategories.map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

function SearchFilter({ query, onSearchFiles }) {
  const theme = useTheme();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    onSearchFiles(value);
  };

  return (
    <Box flex={1}>
      <TextField
        size="small"
        fullWidth
        variant="outlined"
        placeholder="Search files"
        value={query}
        onChange={handleSearchChange}
        slotProps={{
          input: {
            startAdornment: (
              <SearchRounded sx={{ color: theme.palette.action.active }} />
            ),
            style: {
              borderRadius: 8,
              maxWidth: 391,
            },
          },
        }}
      />
    </Box>
  );
}

function FileTypeFilter({ fileType, onFileTypeChange }) {
  const fileTypeOptions = {
    all: { label: "All" },
    docx: { label: "Docs" },
    txt: { label: "Txt" },
    pdf: { label: "Pdf" },
    "png/jpg/jpeg": { label: "Image" },
    "csv/xls/xlsx": { label: "Spreadsheet" },
  };
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="File Type"
      size="small"
      value={fileType}
      onChange={(e) => onFileTypeChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {Object.keys(fileTypeOptions).map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {fileTypeOptions[option].label}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

function SortFilter({ sortOption, onSortFiles, isDarkMode }) {
  const theme = useTheme();
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const posthog = usePostHog();
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    posthog.capture(OPEN_SORT_MENU_ON_FILE_EXPLORER);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <Fragment>
      <Button
        variant="contained"
        onClick={handleClick}
        startIcon={
          open ? (
            isDarkMode ? (
              <SortRounded
                fontSize="small"
                sx={{ color: (theme) => theme.palette.text.primary }}
              />
            ) : (
              <GradientIconLight Icon={SortRounded} sx={{ fontSize: 20 }} />
            )
          ) : (
            <SortRounded fontSize="small" />
          )
        }
        className={`${classes.filterButton} ${
          open ? classes.selectedFilterButton : ""
        }`}>
        Sort
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 3,
              boxShadow:
                "0px 11px 15px -7px rgba(0, 0, 0, 0.20), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
            },
          },
        }}>
        {sortOptions.map((option) => {
          const { value, label } = option;
          const isSelected = sortOption === value;
          return (
            <MenuItem
              key={value}
              value={value}
              sx={{
                background: isSelected ? theme.palette.selection : "initial",
              }}
              onClick={() => onSortFiles(value)}>
              <ListItemIcon>
                {isSelected &&
                  (isDarkMode ? (
                    <CheckRounded
                      sx={{ color: (theme) => theme.palette.text.primary }}
                    />
                  ) : (
                    <GradientIconLight Icon={CheckRounded} />
                  ))}
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
}

function StatusFilter({ status, onStatusChange }) {
  const fileStatusOptions = {
    ...fileStatus,
    all: { label: "All", icon: null },
  };
  return (
    <TextField
      select
      slotProps={{
        input: {
          style: { borderRadius: 8 },
        },
      }}
      variant="outlined"
      label="File Status"
      size="small"
      value={status}
      onChange={(e) => onStatusChange(e.target.value)}
      sx={{ minWidth: 160 }}>
      {Object.keys(fileStatusOptions).map((option) => {
        return (
          <MenuItem key={option} value={option}>
            {fileStatusOptions[option].label}
          </MenuItem>
        );
      })}
    </TextField>
  );
}

export default Filters;
